import { usePostInvestigationMessage } from "#src/components/hooks/adapters/useInvestigations";
import { useFile } from "#src/hooks/useFile";
import { isAgentBusy } from "#src/view-models/investigations";
import {
  Button,
  FileUploadInput,
  Form,
  TextAreaInput,
  useForm,
} from "@validereinc/common-components";
import {
  type ArtifactRefType,
  MAX_INVESTIGATION_MESSAGE_LENGTH,
  type InvestigationStatusType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useState } from "react";
import styles from "./FollowUpInput.module.scss";

const cx = classNames.bind(styles);

type FollowUpFormType = {
  content: string;
  attachments: Array<{
    ref: string;
    name: string;
    mime_type?: string;
  }>;
};

export type FollowUpInputProps = {
  hasOpenPendingApproval: boolean;
  status: InvestigationStatusType;
  postMessage: ReturnType<typeof usePostInvestigationMessage>;
  isApprovalLoading: boolean;
};

export const FollowUpInput = ({
  hasOpenPendingApproval,
  status,
  postMessage,
  isApprovalLoading,
}: FollowUpInputProps) => {
  const form = useForm<FollowUpFormType>({
    mode: "onChange",
    defaultValues: { content: "", attachments: [] },
  });

  const { handleFileUpload, handleFileDownload } = useFile();
  const [attachmentUploadInFlightCount, setAttachmentUploadInFlightCount] =
    useState(0);
  const isAttachmentProcessing = attachmentUploadInFlightCount > 0;

  const agentBusy = isAgentBusy(status);
  const isDisabled = agentBusy || postMessage.isLoading || isApprovalLoading;
  const followUpContent = form.watch("content");
  const attachments = form.watch("attachments") ?? [];
  const hasSubmittableContent = Boolean(
    followUpContent.trim() || attachments.length
  );

  const uploadAttachment = async (
    ...args: Parameters<typeof handleFileUpload>
  ) => {
    const [file] = args;
    setAttachmentUploadInFlightCount((count) => count + 1);
    try {
      const uploadedFile = await handleFileUpload(...args);
      if (!uploadedFile || !file) return uploadedFile;

      return {
        ...uploadedFile,
        mime_type: file.type || "application/octet-stream",
      };
    } finally {
      setAttachmentUploadInFlightCount((count) => Math.max(0, count - 1));
    }
  };

  const handleSubmit = form.handleSubmit(({ content }) => {
    if (!hasSubmittableContent) return;

    const artifacts: ArtifactRefType[] = attachments.map((attachment) => ({
      kind: "dp_file",
      name: attachment.name,
      mime_type: attachment.mime_type ?? "application/octet-stream",
      ref: {
        file_id: attachment.ref,
      },
    }));

    postMessage.mutate(
      {
        kind: "message",
        content: content.trim() || " ",
        metadata: artifacts.length ? { artifacts } : undefined,
      },
      {
        onSuccess: () => {
          form.reset({ content: "", attachments: [] });
        },
      }
    );
  });

  const onSubmit = () => {
    void handleSubmit();
  };

  return (
    <Form {...form}>
      <TextAreaInput
        name="content"
        label="Follow-up"
        isLabelShown={false}
        placeholder={
          agentBusy
            ? "Waiting for the agent to finish..."
            : hasOpenPendingApproval
              ? "Add a note (or approve/reject above)..."
              : "Send a follow-up to continue the investigation..."
        }
        isFluid
        isDisabled={isDisabled}
        rules={{
          validate: {
            maxLength: (value: string) =>
              value.length <= MAX_INVESTIGATION_MESSAGE_LENGTH ||
              `Follow-up must be ${MAX_INVESTIGATION_MESSAGE_LENGTH.toLocaleString()} characters or fewer.`,
          },
        }}
      />

      <div className={cx("follow-up-input__actions-cluster")}>
        <div className={cx("follow-up-input__attachment-field")}>
          <FileUploadInput
            name="attachments"
            id="attachments"
            label="Attachments"
            isLabelShown={false}
            isRequired={false}
            allowMultiple
            hasTrigger
            uploadFile={uploadAttachment}
            downloadFile={handleFileDownload}
          />
        </div>

        <Button
          variant="primary"
          size="small"
          isLoading={postMessage.isLoading}
          disabled={
            isDisabled ||
            isAttachmentProcessing ||
            !hasSubmittableContent ||
            !form.formState.isValid
          }
          onClick={onSubmit}
        >
          Send
        </Button>
      </div>
    </Form>
  );
};
